import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import { useSearchParamsStore } from '@aibees/elephant/store/search-params-store';
import { useCallback, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import type { SearchBrowseParams } from '../types';

type QueryStringConfig<T> = {
  /** Subscribe only to specific keys within the scope's params.
   *  When provided, the component only re-renders when these keys change. */
  select?: Array<keyof T & string>;
};

const EMPTY_SLICE = {
  params: {} as Partial<SearchBrowseParams>,
  previous: undefined as Partial<SearchBrowseParams> | undefined,
};

/**
 * Returns a stable array reference as long as the contents (shallow) haven't changed.
 * Prevents the Zustand selector from being recreated on every render when the
 * caller passes `select` as an inline array literal.
 */
function useStableArray<T>(arr: T[] | undefined): T[] | undefined {
  const ref = useRef(arr);
  if (arr === ref.current) return ref.current;
  const prev = ref.current;
  if (
    arr == null ||
    prev == null ||
    arr.length !== prev.length ||
    arr.some((v, i) => v !== prev[i])
  ) {
    ref.current = arr;
  }
  return ref.current;
}

export function useSearchQueryFilter<
  T extends SearchBrowseParams = SearchBrowseParams
>(config?: QueryStringConfig<T>) {
  const scope = useSearchTableScope();
  const selectKeys = useStableArray(config?.select);

  // When selectKeys is provided, return only { params } so subscribers don't
  // re-render when previousParamsByScope changes (only when their params slice changes).
  // When no select, return { params, previous } for consumers that need previousParams.
  const selector = useCallback(
    (state: {
      paramsByScope: Record<SearchTableScope, Partial<SearchBrowseParams>>;
      previousParamsByScope: Partial<
        Record<SearchTableScope, Partial<SearchBrowseParams>>
      >;
    }) => {
      if (scope !== 'company' && scope !== 'people' && scope !== 'employees')
        return EMPTY_SLICE;
      const allParams = state.paramsByScope[scope] ?? {};
      const params =
        selectKeys && selectKeys.length > 0
          ? selectKeys.reduce((acc, key) => {
              if (key in allParams)
                acc[key as keyof SearchBrowseParams] =
                  allParams[key as keyof SearchBrowseParams];
              return acc;
            }, {} as Partial<SearchBrowseParams>)
          : allParams;
      if (selectKeys && selectKeys.length > 0) {
        return { params };
      }
      return {
        params,
        previous: state.previousParamsByScope[scope],
      };
    },
    [scope, selectKeys]
  );
  // When selectKeys is used, compare params by value so subscribers only re-render
  // when their selected keys actually change (selector always returns new params object).
  const equalityFn =
    selectKeys && selectKeys.length > 0
      ? (
          a: { params: Partial<SearchBrowseParams> },
          b: { params: Partial<SearchBrowseParams> }
        ) => shallow(a.params, b.params)
      : shallow;
  const scopeSlice = useSearchParamsStore(selector, equalityFn);
  const setParamsForScope = useSearchParamsStore.useSetParamsForScope();
  const setParamForScope = useSearchParamsStore.useSetParamForScope();
  const removeParamForScope = useSearchParamsStore.useRemoveParamForScope();
  const removeAllParamsForScope =
    useSearchParamsStore.useRemoveAllParamsForScope();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';

  const params = scopeSlice.params as Partial<T>;
  const previousParams = (
    'previous' in scopeSlice ? scopeSlice.previous : undefined
  ) as Partial<T> | undefined;

  const setParams = useCallback(
    (values?: Partial<T>, skipHistory?: boolean, undoredo?: boolean) => {
      if (!hasScope) return;
      setParamsForScope(
        scope as SearchTableScope,
        values as Partial<SearchBrowseParams> | undefined,
        { replace: undoredo, skipHistory }
      );
    },
    [hasScope, scope, setParamsForScope]
  );

  const setParam = useCallback(
    (name: keyof T & string, value?: string, skipHistory?: boolean) => {
      if (!hasScope) return;
      setParamForScope(scope as SearchTableScope, name, value, {
        skipHistory,
      });
    },
    [hasScope, scope, setParamForScope]
  );

  const removeParam = useCallback(
    (name: keyof T & string, skipHistory?: boolean) => {
      if (!hasScope) return;
      removeParamForScope(scope as SearchTableScope, name, { skipHistory });
    },
    [hasScope, scope, removeParamForScope]
  );

  const removeAllParams = useCallback(() => {
    if (!hasScope) return;
    removeAllParamsForScope(scope as SearchTableScope);
  }, [hasScope, scope, removeAllParamsForScope]);

  const getParam = useCallback(
    (name: string, defaultValue: string | null = null) =>
      (params[name] as string | undefined) ?? defaultValue,
    [params]
  );

  const replaceParams = useCallback(
    (values?: Partial<T>) => {
      if (!hasScope) return;
      replaceParamsForScope(
        scope as SearchTableScope,
        values as Partial<SearchBrowseParams> | undefined
      );
    },
    [hasScope, scope, replaceParamsForScope]
  );

  return useMemo(
    () => ({
      params: params as T,
      setParams,
      setParam,
      removeParam,
      removeAllParams,
      getParam,
      previousParams: previousParams as Partial<T> | undefined,
      replaceParams,
    }),
    [
      params,
      setParams,
      setParam,
      removeParam,
      removeAllParams,
      getParam,
      previousParams,
      replaceParams,
    ]
  );
}

/**
 * Returns only setters for the current search scope's params, without subscribing
 * to paramsByScope. Use when a component only needs to update params (e.g. clear
 * tags) and should not re-render on every param change.
 */
export function useSearchQueryFilterSetters<
  T extends SearchBrowseParams = SearchBrowseParams
>() {
  const scope = useSearchTableScope();
  const setParamsForScope = useSearchParamsStore.useSetParamsForScope();
  const setParamForScope = useSearchParamsStore.useSetParamForScope();
  const removeParamForScope = useSearchParamsStore.useRemoveParamForScope();
  const removeAllParamsForScope =
    useSearchParamsStore.useRemoveAllParamsForScope();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';

  const setParams = useCallback(
    (values?: Partial<T>, skipHistory?: boolean, undoredo?: boolean) => {
      if (!hasScope) return;
      setParamsForScope(
        scope as SearchTableScope,
        values as Partial<SearchBrowseParams> | undefined,
        { replace: undoredo, skipHistory }
      );
    },
    [hasScope, scope, setParamsForScope]
  );

  const setParam = useCallback(
    (name: keyof T & string, value?: string, skipHistory?: boolean) => {
      if (!hasScope) return;
      setParamForScope(scope as SearchTableScope, name, value, {
        skipHistory,
      });
    },
    [hasScope, scope, setParamForScope]
  );

  const removeParam = useCallback(
    (name: keyof T & string, skipHistory?: boolean) => {
      if (!hasScope) return;
      removeParamForScope(scope as SearchTableScope, name, { skipHistory });
    },
    [hasScope, scope, removeParamForScope]
  );

  const removeAllParams = useCallback(() => {
    if (!hasScope) return;
    removeAllParamsForScope(scope as SearchTableScope);
  }, [hasScope, scope, removeAllParamsForScope]);

  const replaceParams = useCallback(
    (values?: Partial<T>) => {
      if (!hasScope) return;
      replaceParamsForScope(
        scope as SearchTableScope,
        values as Partial<SearchBrowseParams> | undefined
      );
    },
    [hasScope, scope, replaceParamsForScope]
  );

  return useMemo(
    () => ({
      setParams,
      setParam,
      removeParam,
      removeAllParams,
      replaceParams,
    }),
    [setParams, setParam, removeParam, removeAllParams, replaceParams]
  );
}
